import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import {
  Table,
  Button,
  InputNumber,
  Input,
  Space,
  message,
  Alert,
  Tag,
  Modal,
  Statistic,
  Row,
  Col,
} from "antd";
import { MailOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { NavLink } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";

function ReengageUsers() {
  const UserID = localStorage.getItem("AdminID");
  const AccessKey = localStorage.getItem("AdminAccessKey");

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [inactiveDays, setInactiveDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [cutoffDate, setCutoffDate] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [emailFilter, setEmailFilter] = useState("");

  const loadInactive = () => {
    setLoading(true);
    setSelectedKeys([]);
    axios({
      method: "post",
      url: config.base_url + "AdminUser/GetInactivePumpsForReengage",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        InactiveDays: inactiveDays || 30,
      },
    })
      .then((response) => {
        if (response.data.status_code == 1) {
          setRows(response.data.listofRecords || []);
          setCutoffDate(response.data.CutoffDate || "");
        } else {
          message.error(response.data.status_message || "Unable to load list.");
          setRows([]);
        }
      })
      .catch(() => message.error("Network error loading inactive pumps."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "Re-engage Inactive Users";
    loadInactive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterText = (emailFilter || "").trim().toLowerCase();
  const filteredRows = !filterText
    ? rows
    : rows.filter((r) => {
        const email = (r.Email || "").toLowerCase();
        const name = (r.Name || "").toLowerCase();
        const code = (r.PumpCode || "").toLowerCase();
        const mobile = (r.Mobile || "").toLowerCase();
        return (
          email.includes(filterText) ||
          name.includes(filterText) ||
          code.includes(filterText) ||
          mobile.includes(filterText)
        );
      });

  const sendEmails = () => {
    const withEmail = selectedKeys.filter((id) => {
      const row = rows.find((r) => r.PumpID === id);
      return row && row.HasEmail;
    });

    if (!withEmail.length) {
      message.warning("Select pumps that have a valid email address.");
      return;
    }

    if (withEmail.length > 100) {
      message.warning("Please send in batches of max 100 emails at a time.");
      return;
    }

    const selectedNames = withEmail
      .map((id) => {
        const row = rows.find((r) => r.PumpID === id);
        return row ? `${row.Name} (${row.Email})` : id;
      })
      .slice(0, 5);

    Modal.confirm({
      title: `Send re-engagement email to ${withEmail.length} pump(s)?`,
      content: (
        <div>
          <p>
            Each email includes Pump Code, Email, Mobile, Password, benefits,
            and a login invite. Max 100 per batch.
          </p>
          {withEmail.length === 1 ? (
            <p>
              <strong>Recipient:</strong> {selectedNames[0]}
            </p>
          ) : (
            <p>
              <strong>Examples:</strong> {selectedNames.join(", ")}
              {withEmail.length > 5 ? "…" : ""}
            </p>
          )}
        </div>
      ),
      okText: withEmail.length === 1 ? "Send test email" : "Send emails",
      cancelText: "Cancel",
      onOk: () => {
        setSending(true);
        setLastResult(null);
        return axios({
          method: "post",
          url: config.base_url + "AdminUser/SendReengagementEmails",
          headers: { "Content-Type": "application/json" },
          data: {
            UserID,
            AccessKey,
            InactiveDays: inactiveDays || 30,
            PumpIDs: withEmail,
          },
        })
          .then((response) => {
            if (response.data.status_code == 1) {
              setLastResult(response.data);
              message.success(
                `Sent: ${response.data.Sent}, Failed: ${response.data.Failed}, Skipped: ${response.data.Skipped}`
              );
            } else {
              message.error(
                response.data.status_message || "Unable to send emails."
              );
            }
          })
          .catch(() => message.error("Network error while sending emails."))
          .finally(() => setSending(false));
      },
    });
  };

  const columns = [
    {
      title: "Pump Code",
      dataIndex: "PumpCode",
      key: "PumpCode",
    },
    {
      title: "Pump Name",
      dataIndex: "Name",
      key: "Name",
    },
    {
      title: "Email",
      dataIndex: "Email",
      key: "Email",
      render: (v, record) =>
        record.HasEmail ? v : <Tag color="red">No email</Tag>,
    },
    {
      title: "Mobile",
      dataIndex: "Mobile",
      key: "Mobile",
    },
    {
      title: "Package",
      dataIndex: "PackageName",
      key: "PackageName",
    },
    {
      title: "Last Sale",
      dataIndex: "LastSaleDate",
      key: "LastSaleDate",
      render: (v, record) =>
        record.NeverSold
          ? "Never sold"
          : v
          ? dayjs(v).format(config.date_format)
          : "-",
    },
    {
      title: "Days Inactive",
      dataIndex: "DaysInactive",
      key: "DaysInactive",
      sorter: (a, b) => a.DaysInactive - b.DaysInactive,
      defaultSortOrder: "descend",
    },
  ];

  const withEmailCount = selectedKeys.filter((id) =>
    rows.some((r) => r.PumpID === id && r.HasEmail)
  ).length;

  return (
    <div className="right-side-contents">
      <div className="page-content control-base-wrap">
        <div className="page-header">
          <h3 className="page-title">
            <NavLink to="/controlbase/dashboard">
              <ChevronLeftIcon />
            </NavLink>
            Re-engage Inactive Users
          </h3>
          <div className="header-actions">
            <Space wrap>
              <span>Inactive if no sale in last</span>
              <InputNumber
                min={7}
                max={365}
                value={inactiveDays}
                onChange={(v) => setInactiveDays(v || 30)}
              />
              <span>days</span>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadInactive}
                loading={loading}
              >
                Refresh list
              </Button>
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={sendEmails}
                loading={sending}
                disabled={!withEmailCount}
              >
                Send emails ({withEmailCount})
              </Button>
            </Space>
          </div>
        </div>

        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="Campaign rules"
          description={
            <>
              Shows pumps with no sale invoice on/after{" "}
              <strong>{cutoffDate || "cutoff"}</strong> (or never sold). Use the
              email filter below to find one company, select it, and send a test
              email first. Full batch max is 100.
            </>
          }
        />

        <Space wrap style={{ marginBottom: 16, width: "100%" }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Filter by email, pump name, code, or mobile"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            style={{ width: 360, maxWidth: "100%" }}
          />
          {filterText ? (
            <Tag color="blue">
              Showing {filteredRows.length} of {rows.length}
            </Tag>
          ) : null}
          {filteredRows.length === 1 && filteredRows[0].HasEmail ? (
            <Button
              size="small"
              type="dashed"
              onClick={() => setSelectedKeys([filteredRows[0].PumpID])}
            >
              Select this company
            </Button>
          ) : null}
        </Space>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Statistic title="Inactive pumps" value={rows.length} />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="With email"
              value={rows.filter((r) => r.HasEmail).length}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Never sold"
              value={rows.filter((r) => r.NeverSold).length}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="Selected" value={withEmailCount} />
          </Col>
        </Row>

        {lastResult ? (
          <Alert
            style={{ marginBottom: 16 }}
            type={lastResult.Failed > 0 ? "warning" : "success"}
            showIcon
            message={`Last send — Sent ${lastResult.Sent}, Failed ${lastResult.Failed}, Skipped ${lastResult.Skipped}`}
          />
        ) : null}

        <Table
          size="small"
          rowKey="PumpID"
          loading={loading || sending}
          dataSource={filteredRows}
          columns={columns}
          bordered
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys),
            getCheckboxProps: (record) => ({
              disabled: !record.HasEmail,
            }),
          }}
        />
      </div>
    </div>
  );
}

export default ReengageUsers;
